import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { createCheckoutSession, handleStripePortalRedirect } from '../services/api';
import { Currency, Interval, getTier } from '../Data/PricingData';
import { Tier } from '../types/user';
import { useAnalytics } from './useAnalytics';

const CURRENCY_CACHE_KEY = 'sb_pricing_currency';
const EU_COUNTRIES = new Set([
  'AT', 'BE', 'CY', 'DE', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'IE', 'IT',
  'LT', 'LU', 'LV', 'MT', 'NL', 'PT', 'SI', 'SK',
]);

let currencyRequest: Promise<Currency> | null = null;

const readCachedCurrency = (): Currency | null => {
  try {
    const v = sessionStorage.getItem(CURRENCY_CACHE_KEY);
    return v === 'usd' || v === 'cad' || v === 'eur' ? v : null;
  } catch {
    return null;
  }
};

const localeCurrency = (): Currency | null => {
  const lang = (navigator.language || '').toLowerCase();
  return lang.endsWith('-ca') ? 'cad' : null;
};

const detectCurrency = (): Promise<Currency> => {
  const cached = readCachedCurrency();
  if (cached) return Promise.resolve(cached);
  if (!currencyRequest) {
    currencyRequest = fetch('https://ipapi.co/json/')
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(r.statusText))))
      .then((data): Currency => {
        if (data.country_code === 'CA') return 'cad';
        if (EU_COUNTRIES.has(data.country_code)) return 'eur';
        return 'usd';
      })
      .catch(() => localeCurrency() ?? 'usd')
      .then(c => {
        try { sessionStorage.setItem(CURRENCY_CACHE_KEY, c); } catch { /* ignore */ }
        return c;
      });
  }
  return currencyRequest;
};

/** Best-guess billing currency, detected once per session. */
export const useCurrency = (initial?: Currency | null, enabled = true) => {
  const [currency, setCurrency] = useState<Currency>(
    initial ?? readCachedCurrency() ?? localeCurrency() ?? 'usd'
  );
  const userPicked = useRef(false);

  useEffect(() => {
    if (!enabled || initial || userPicked.current) return;
    let cancelled = false;
    detectCurrency().then(c => { if (!cancelled && !userPicked.current) setCurrency(c); });
    return () => { cancelled = true; };
  }, [initial, enabled]);

  const choose = useCallback((c: Currency) => {
    userPicked.current = true;
    setCurrency(c);
  }, []);

  return [currency, choose] as const;
};

export interface CheckoutRequest {
  tier: Tier;
  interval: Interval;
  currency: Currency;
  promoCode?: string;
  /** Where to land after sign-in. Defaults to /pricing with the choice encoded so checkout resumes. */
  returnTo?: string;
}

export const buildResumeUrl = ({ tier, interval, currency, promoCode }: CheckoutRequest) => {
  const params = new URLSearchParams({ tier, interval, currency });
  if (promoCode) params.set('promo', promoCode);
  return `/pricing?${params.toString()}`;
};

/**
 * One checkout path for every upgrade surface: signs the visitor in (then
 * resumes), sends existing subscribers to the billing portal, otherwise opens
 * Stripe Checkout.
 */
export const useCheckout = (source: string) => {
  const { isAuthenticated, subscription, login } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { trackCheckoutStarted } = useAnalytics();
  const [pendingTier, setPendingTier] = useState<Tier | null>(null);

  // Returning from Stripe with the back button restores this page from the
  // bfcache with the spinner still showing.
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => { if (e.persisted) setPendingTier(null); };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  const openBillingPortal = useCallback(async () => {
    try {
      await handleStripePortalRedirect(window.location.href);
      return true;
    } catch (error: any) {
      console.error('Error opening billing portal:', error);
      toast('Could not open billing. Please try again.', 'error');
      return false;
    }
  }, [toast]);

  const startCheckout = useCallback(async (req: CheckoutRequest) => {
    const { tier, interval, currency, promoCode } = req;

    if (!isAuthenticated) {
      setPendingTier(tier);
      login({ appState: { returnTo: tier === 'free' ? '/' : (req.returnTo ?? buildResumeUrl(req)) } });
      return;
    }

    if (tier === 'free') {
      if (subscription) {
        setPendingTier(tier);
        if (!(await openBillingPortal())) setPendingTier(null);
      } else {
        navigate('/');
      }
      return;
    }

    setPendingTier(tier);

    if (subscription) {
      if (!(await openBillingPortal())) setPendingTier(null);
      return;
    }

    try {
      const response = await createCheckoutSession(tier, interval, currency, window.location.href, promoCode || undefined);
      if (!response?.url) throw new Error('Could not start checkout. Please try again.');

      if (response.currencyLocked && response.appliedCurrency && response.appliedCurrency !== currency) {
        const applied = response.appliedCurrency.toUpperCase();
        toast(`Your account is already billed in ${applied}, so checkout will use ${applied}.`, 'info');
      }

      const plan = getTier(tier);
      const appliedCurrency = (response.appliedCurrency ?? currency) as Currency;
      trackCheckoutStarted({
        plan: tier,
        interval: interval === 'annual' ? 'yearly' : 'monthly',
        price: plan?.price[appliedCurrency]?.[interval],
        currency: appliedCurrency,
        source,
      });

      window.location.href = response.url;
    } catch (error: any) {
      console.error('Error creating checkout session:', error);
      toast(error?.details || error?.message || 'Could not start checkout. Please try again.', 'error');
      setPendingTier(null);
    }
  }, [isAuthenticated, subscription, login, navigate, toast, openBillingPortal, trackCheckoutStarted, source]);

  return { startCheckout, openBillingPortal, pendingTier };
};
